import { useEffect, useState, useCallback, useRef, useMemo, useLayoutEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import MemberShell from "@/components/MemberShell";
import DossierDetail from "@/pages/DossierDetail";
import { useAuth } from "@/context/AuthContext";
import { MEMBER_DASH_NAV_BASE } from "@/lib/memberDashboardNav";
import DossierCard from "@/components/DossierCard";
import DossierHoverPreview from "@/components/DossierHoverPreview";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, X, Store, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { DEFAULT_DESCRIPTION } from "@/config/site";
import { buildMarketplaceItemListJsonLd } from "@/lib/seoJsonLd";
import { cn } from "@/lib/utils";
import { listScrollKey, saveScrollForList, restoreScrollForList } from "@/lib/scrollRestore";

/** Filtres marketplace : même police / taille que les Select (écart mobile Input vs text-sm). */
const MP_FILTER_TRIGGER = "h-11 bg-white text-sm font-sans";
const MP_FILTER_INPUT = "h-11 bg-white text-sm font-sans";

const initialFilters = () => ({
  q: "",
  secteur: "",
  produit_id: "",
  annonceType: "all",
  location: "",
  min_price: "",
  max_price: "",
  custom: {},
});

export default function Marketplace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const memberLayout = Boolean(user);
  const marketDossierId = useMemo(() => {
    const raw = searchParams.get("dossier");
    return raw && String(raw).trim() ? String(raw).trim() : null;
  }, [searchParams]);

  const marketplaceScrollKey = useMemo(
    () => listScrollKey("/marketplace", searchParams.toString()),
    [searchParams],
  );

  const buildMarketDossierHref = useCallback(
    (dossierId) => {
      const sp = new URLSearchParams(searchParams);
      sp.set("dossier", String(dossierId));
      const q = sp.toString();
      return q ? `/marketplace?${q}` : "/marketplace";
    },
    [searchParams],
  );

  const onSaveMarketplaceScroll = useCallback(() => {
    saveScrollForList(marketplaceScrollKey);
  }, [marketplaceScrollKey]);

  useLayoutEffect(() => {
    if (marketDossierId) return;
    restoreScrollForList(marketplaceScrollKey);
  }, [marketDossierId, marketplaceScrollKey]);

  const closeMarketDossier = useCallback(() => {
    const sp = new URLSearchParams(searchParams);
    sp.delete("dossier");
    const q = sp.toString();
    navigate({ pathname: "/marketplace", search: q ? `?${q}` : "" });
  }, [navigate, searchParams]);
  const [dossiers, setDossiers] = useState([]);
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const marketplaceJsonLd = useMemo(() => buildMarketplaceItemListJsonLd(dossiers), [dossiers]);
  const [filters, setFilters] = useState(initialFilters);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchDossiers = useCallback(async (override) => {
    const f = override !== undefined ? override : filtersRef.current;
    setLoading(true);
    const params = {};
    if (f.q) params.q = f.q;
    if (f.secteur) params.secteur = f.secteur;
    if (f.produit_id) params.produit_id = f.produit_id;
    if (f.annonceType && f.annonceType !== "all") params.type = f.annonceType;
    if (f.location) params.location = f.location;
    if (f.min_price !== "") params.min_price = f.min_price;
    if (f.max_price !== "") params.max_price = f.max_price;
    const customEntries = Object.fromEntries(
      Object.entries(f.custom || {}).filter(([, v]) => v != null && String(v).trim() !== "")
    );
    if (Object.keys(customEntries).length > 0) {
      params.custom_fields_filters = JSON.stringify(customEntries);
    }
    try {
      const { data } = await api.get("/dossiers", { params });
      setDossiers(Array.isArray(data) ? data : []);
    } catch (e) {
      setDossiers([]);
      const d = e?.response?.data?.detail || e?.message;
      if (typeof d === "string" && d) toast.error(d);
      else toast.error("Impossible de charger la marketplace.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/produits").then(({ data }) => setProduits(data)).catch(() => {});
  }, []);

  // Filtres dynamiques: refetch automatique (avec léger debounce).
  useEffect(() => {
    const t = setTimeout(() => {
      fetchDossiers(filters);
    }, 350);
    return () => clearTimeout(t);
  }, [filters, fetchDossiers]);

  const secteurs = useMemo(
    () => [...new Set(produits.map((p) => p.secteur).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fr")),
    [produits]
  );

  const produitsDuSecteur = useMemo(() => {
    if (!filters.secteur) return produits;
    return produits.filter((p) => p.secteur === filters.secteur);
  }, [produits, filters.secteur]);

  const produitSelectValue = useMemo(() => {
    const id = String(filters.produit_id ?? "").trim();
    if (!id) return "all";
    return produits.some((p) => String(p.produit_id) === id) ? id : "all";
  }, [filters.produit_id, produits]);

  const produitSelectionne = useMemo(() => {
    const id = String(filters.produit_id ?? "").trim();
    if (!id) return null;
    return produits.find((p) => String(p.produit_id) === id) || null;
  }, [produits, filters.produit_id]);

  const extraFiltersActiveCount = useMemo(() => {
    let n = 0;
    if (String(filters.location || "").trim()) n += 1;
    if (String(filters.min_price ?? "").trim()) n += 1;
    if (String(filters.max_price ?? "").trim()) n += 1;
    for (const v of Object.values(filters.custom || {})) {
      if (v != null && String(v).trim() !== "") n += 1;
    }
    return n;
  }, [filters.location, filters.min_price, filters.max_price, filters.custom]);

  const champsDynamiques = useMemo(() => {
    if (!produitSelectionne?.champs?.length) return [];
    const t = filters.annonceType;
    return produitSelectionne.champs.filter((c) => {
      if (c.visible_public === false) return false;
      const ca = c.cible_annonces;
      if (ca == null || ca === "tous") return true;
      if (t === "all" || !t) return true;
      return ca === t;
    });
  }, [produitSelectionne, filters.annonceType]);

  /** Au moins un critère différent du défaut (recherche, secteur, produit, type, prix, champs perso). */
  const hasActiveFilters = useMemo(() => {
    if (String(filters.q || "").trim() !== "") return true;
    if (String(filters.secteur || "").trim() !== "") return true;
    if (String(filters.produit_id || "").trim() !== "") return true;
    if (filters.annonceType && filters.annonceType !== "all") return true;
    if (String(filters.location || "").trim() !== "") return true;
    if (String(filters.min_price ?? "").trim() !== "") return true;
    if (String(filters.max_price ?? "").trim() !== "") return true;
    for (const v of Object.values(filters.custom || {})) {
      if (v != null && String(v).trim() !== "") return true;
    }
    return false;
  }, [filters]);

  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  const onSecteurChange = (v) => {
    setMoreFiltersOpen(false);
    setFilters((prev) => ({
      ...prev,
      secteur: v === "all" ? "" : v,
      produit_id: "",
      custom: {},
    }));
  };

  const onProduitChange = (v) => {
    const nextId = v === "all" ? "" : String(v);
    setFilters((prev) => ({
      ...prev,
      produit_id: nextId,
      custom: {},
    }));
  };

  const onAnnonceTypeChange = (v) => {
    setFilter({ annonceType: v });
  };

  const setCustom = (nom, value) => {
    setFilters((prev) => ({
      ...prev,
      custom: { ...prev.custom, [nom]: value },
    }));
  };

  const clearFilters = () => {
    const empty = initialFilters();
    setMoreFiltersOpen(false);
    setFilters(empty);
    fetchDossiers(empty);
  };

  const marketplaceBody = (
    <>
        {!memberLayout ? (
        <div className="mb-8 sm:mb-10 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2 text-[#2D7FF9]">
              <Store className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={2} aria-hidden />
              <h1 className="font-display text-2xl font-black tracking-tighter sm:text-3xl md:text-4xl lg:text-5xl text-[#0B0D12]">
                Marketplace
              </h1>
            </div>
            <p className="text-slate-600 max-w-3xl">
              Parcourez les <strong>offres</strong> (dossiers en vente) et les <strong>recherches</strong> (acheteurs qui
              cherchent un type de dossier). Filtrez par secteur, produit et critères de qualification du produit.
            </p>
          </div>
          <Button asChild className="btn-primary h-11 shrink-0 rounded-lg px-5 font-semibold sm:self-start w-full sm:w-auto" data-testid="marketplace-publish-cta">
            <Link to="/nouveau-dossier">Publier une annonce</Link>
          </Button>
        </div>
        ) : null}

        <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4 md:p-6 mb-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500 mb-1 block">Type d’annonce</Label>
              <Select modal value={filters.annonceType} onValueChange={onAnnonceTypeChange}>
                <SelectTrigger className={MP_FILTER_TRIGGER} data-testid="market-annonce-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tout : offres et recherches</SelectItem>
                  <SelectItem value="vente">Uniquement les offres (vendeurs / dossiers en vente)</SelectItem>
                  <SelectItem value="recherche">Uniquement les recherches (demandes d’achat)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Secteur</Label>
              <Select modal value={filters.secteur || "all"} onValueChange={onSecteurChange}>
                <SelectTrigger className={MP_FILTER_TRIGGER} data-testid="market-secteur-select">
                  <SelectValue placeholder="Secteur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les secteurs</SelectItem>
                  {secteurs.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Produit / offre RdvMarket</Label>
              <Select modal value={produitSelectValue} onValueChange={onProduitChange}>
                <SelectTrigger className={MP_FILTER_TRIGGER} data-testid="market-produit-select">
                  <SelectValue placeholder="Choisir un produit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les produits du secteur</SelectItem>
                  {produitsDuSecteur.map((p) => {
                    const pid = String(p.produit_id);
                    return (
                      <SelectItem key={pid} value={pid}>
                        {p.nom}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Mots-clés, département (63), ville, localisation…"
              value={filters.q}
              onChange={(e) => setFilter({ q: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && fetchDossiers()}
              className={cn(MP_FILTER_INPUT, "pl-9")}
              data-testid="market-search-input"
            />
          </div>

          <Button
            type="button"
            variant="outline"
            className="h-11 w-full justify-center gap-2 bg-white font-sans text-sm font-medium text-slate-800 sm:w-auto"
            onClick={() => setMoreFiltersOpen((o) => !o)}
            data-testid="market-more-filters-toggle"
            aria-expanded={moreFiltersOpen}
          >
            {moreFiltersOpen ? <Minus className="h-4 w-4 shrink-0" aria-hidden /> : <Plus className="h-4 w-4 shrink-0" aria-hidden />}
            {moreFiltersOpen ? "Moins de filtres" : "Plus de filtres"}
            {extraFiltersActiveCount > 0 ? (
              <span className="rounded-full bg-[#2D7FF9]/15 px-2 py-0.5 text-xs font-semibold text-[#2D7FF9]">
                {extraFiltersActiveCount}
              </span>
            ) : null}
          </Button>

          {moreFiltersOpen ? (
            <div className="space-y-4 border-t border-slate-200 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  placeholder="Localisation / département"
                  value={filters.location}
                  onChange={(e) => setFilter({ location: e.target.value })}
                  className={MP_FILTER_INPUT}
                  data-testid="market-location-input"
                  aria-label="Localisation ou département"
                />
                <Input
                  placeholder="Prix min (€)"
                  type="text"
                  inputMode="decimal"
                  value={filters.min_price}
                  onChange={(e) => setFilter({ min_price: e.target.value })}
                  className={MP_FILTER_INPUT}
                  data-testid="market-min-price"
                />
                <Input
                  placeholder="Prix max (€)"
                  type="text"
                  inputMode="decimal"
                  value={filters.max_price}
                  onChange={(e) => setFilter({ max_price: e.target.value })}
                  className={MP_FILTER_INPUT}
                  data-testid="market-max-price"
                />
              </div>

              {produitSelectionne && champsDynamiques.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-slate-700">
                    Critères — {produitSelectionne.nom}
                    <span className="font-normal text-slate-500"> (optionnel)</span>
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {champsDynamiques.map((c) => {
                  const v = filters.custom[c.nom] ?? "";
                  const listOptions = Array.isArray(c.options) ? c.options.map((o) => String(o).trim()).filter(Boolean) : [];
                  if (c.type === "list" && listOptions.length > 0) {
                    return (
                      <div key={c.champ_id ?? c.nom} className="space-y-1">
                        <Label className="text-xs text-slate-600">{c.nom}</Label>
                        <Select
                          modal
                          value={v && listOptions.includes(String(v)) ? String(v) : "all"}
                          onValueChange={(x) => setCustom(c.nom, x === "all" ? "" : x)}
                        >
                          <SelectTrigger className="h-10 bg-white text-sm font-sans">
                            <SelectValue placeholder="Tous" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Tous</SelectItem>
                            {listOptions.map((o) => (
                              <SelectItem key={o} value={o}>
                                {o}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  }
                  if (c.type === "boolean") {
                    return (
                      <div key={c.champ_id ?? c.nom} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                        <span className="text-xs text-slate-700 pr-2">{c.nom}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-slate-500">{v === "true" ? "Oui" : v === "false" ? "Non" : "Tous"}</span>
                          <Select
                            modal
                            value={v || "all"}
                            onValueChange={(x) => setCustom(c.nom, x === "all" ? "" : x)}
                          >
                            <SelectTrigger className="h-8 w-[7rem] text-xs font-sans" data-testid={`market-cf-bool-${c.nom}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Tous</SelectItem>
                              <SelectItem value="true">Oui</SelectItem>
                              <SelectItem value="false">Non</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    );
                  }
                  if (c.type === "number") {
                    return (
                      <div key={c.champ_id ?? c.nom} className="space-y-1">
                        <Label className="text-xs text-slate-600">{c.nom}</Label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          className="h-10 bg-white text-sm font-sans"
                          value={v}
                          onChange={(e) => setCustom(c.nom, e.target.value)}
                          placeholder="Valeur"
                        />
                      </div>
                    );
                  }
                  return (
                    <div key={c.champ_id ?? c.nom} className="space-y-1">
                      <Label className="text-xs text-slate-600">{c.nom}</Label>
                      <Input
                        className="h-10 bg-white text-sm font-sans"
                        value={v}
                        onChange={(e) => setCustom(c.nom, e.target.value)}
                        placeholder="Contient…"
                      />
                    </div>
                  );
                })}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {hasActiveFilters ? (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button variant="outline" className="h-11" onClick={clearFilters} data-testid="market-clear-filters">
                <X className="w-4 h-4 mr-1" />
                Réinitialiser
              </Button>
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white animate-pulse"
              >
                <div className="h-14 bg-slate-100" />
                <div className="space-y-3 p-4">
                  <div className="h-8 w-32 rounded bg-slate-100" />
                  <div className="h-5 rounded bg-slate-100" />
                  <div className="h-4 w-4/5 rounded bg-slate-100" />
                </div>
              </div>
            ))}
          </div>
        ) : dossiers.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-16 text-center">
            <Store className="mx-auto h-10 w-10 text-slate-300" aria-hidden />
            <div className="mt-3 font-display text-xl font-bold text-slate-900">Aucune annonce ne correspond</div>
            <p className="mt-1 text-sm text-slate-500">
              Élargissez le secteur, le type d’annonce ou les critères de qualification.
            </p>
          </div>
        ) : (
          <>
            <p className="mb-5 text-sm font-medium text-slate-700" data-testid="market-results-count">
              <span className="font-display text-lg font-bold tabular-nums text-[#0B0D12]">{dossiers.length}</span>
              {" "}
              annonce{dossiers.length > 1 ? "s" : ""} trouvée{dossiers.length > 1 ? "s" : ""}
            </p>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3 [&>*]:overflow-visible">
              {dossiers.map((d) => {
                const cardHref = memberLayout
                  ? buildMarketDossierHref(d.dossier_id)
                  : `/dossier/${d.dossier_id}`;
                return (
                  <DossierHoverPreview
                    key={d.dossier_id}
                    dossier={d}
                    dossierId={d.dossier_id}
                    produits={produits}
                    href={cardHref}
                    onBeforeOpen={onSaveMarketplaceScroll}
                  >
                    <DossierCard
                      dossier={d}
                      to={memberLayout ? cardHref : undefined}
                      onBeforeOpen={onSaveMarketplaceScroll}
                    />
                  </DossierHoverPreview>
                );
              })}
            </div>
          </>
        )}
    </>
  );

  if (memberLayout) {
    return (
      <>
        <Seo
          title="Marketplace"
          description={DEFAULT_DESCRIPTION}
          canonicalPath="/marketplace"
          jsonLd={marketplaceJsonLd}
        />
        <MemberShell
          title={marketDossierId ? "Fiche annonce" : "Marketplace"}
          subtitle={
            marketDossierId
              ? "Consultation dans votre espace — la navigation reste à gauche."
              : "Parcourez les offres et les recherches, filtrez par secteur et produit."
          }
          dashboardNav={MEMBER_DASH_NAV_BASE}
        >
          {marketDossierId ? (
            <div
              className="mx-auto max-w-7xl overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm"
              data-testid="marketplace-embedded-dossier"
            >
              <DossierDetail embedDossierId={marketDossierId} onEmbedClose={closeMarketDossier} />
            </div>
          ) : (
            <div className="mx-auto max-w-7xl">{marketplaceBody}</div>
          )}
        </MemberShell>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Marketplace"
        description={DEFAULT_DESCRIPTION}
        canonicalPath="/marketplace"
        jsonLd={marketplaceJsonLd}
      />
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12">{marketplaceBody}</div>
    </div>
  );
}
